type TExtensions = 'jpg' | 'jpeg' | 'png' | 'svg' | 'webp';
type TCompressions = 'xl' | 'l' | 'm' | 's' | 'xs' | 't';

interface IImageCompressor {
  readonly supportedImagesExtensions: TExtensions[];
  readonly compressions: TCompressions[];
  parse: (source: string) => TParsedObject;
  getCompressValue: (obj: TDeviseTypObj) => number;
  getCompressedSrc: (obj: TParsedObject, compressionRate: number) => string;
}

type TParsedObject = {
  defaultSource: string;
  path: string;
  name: string;
  extension: TExtensions;
  valid?: boolean;
};

type TDeviseTypObj = {
  isMobile: boolean;
  isTablet: boolean;
  isRetina: boolean;
  compression: number;
};

interface ILazyLoad {
  removeObserver: () => void;
}

const LAZY_LOAD_IMAGE_SELECTOR = 'img.lazy-load[data-src]';

class ImageCompressor implements IImageCompressor {
  private readonly hosts: string[];
  readonly supportedImagesExtensions: TExtensions[];
  readonly compressions: TCompressions[];

  constructor(host: string[], supportedImagesExtensions: TExtensions[], compressions: TCompressions[]) {
    this.hosts = host;
    this.supportedImagesExtensions = supportedImagesExtensions;
    this.compressions = compressions;
  }

  parse = (source: string) => {
    // don't change order!
    if (source.includes('https') && window.location.protocol === 'http:') {
      source = source.replace('https', 'http');
    }
    let [defaultSource, path, name, extension] = /(^.*[\\\/])(.{1,})\.(.+)$/g.exec(source) || [source];

    const data: TParsedObject = {
      defaultSource,
      path,
      name,
      extension: extension as TExtensions,
    };

    data.valid =
      this.supportedImagesExtensions.includes(data.extension) &&
      this.hosts.some((host) => data.defaultSource.includes(host));

    return data;
  };

  getCompressValue = ({ compression = 1, isMobile = false, isRetina = false, isTablet = false }: TDeviseTypObj) => {
    let output = compression;

    // decrease if retina
    if (isRetina) output -= 1;
    // increase if tablet or mobile
    if (isTablet) output += 2;
    if (isMobile) output += 3;

    return Math.max(0, output);
  };

  getCompressedSrc = ({ defaultSource, extension, name, path, valid }: TParsedObject, compressionRate: number) => {
    if (valid) {
      const compression = this.compressions[compressionRate] || this.compressions[0];

      return `${path}${name}-${compression}.${extension}`;
    }

    return defaultSource;
  };
}

const host = ['//f.sravni.ru', '//f.master.qa.sravni-team.ru'];
const SUPPORTED_IMAGES_EXTENSIONS: TExtensions[] = ['jpg', 'jpeg', 'png', 'svg', 'webp'];
const COMPRESSIONS: TCompressions[] = ['xl', 'l', 'm', 's', 'xs', 't'];

const imageCompressor = new ImageCompressor(host, SUPPORTED_IMAGES_EXTENSIONS, COMPRESSIONS);

const imagesCache = new Map();

interface IOptions {
  removeAfterLoaded?: boolean;
  viewportOffset?: number;
}

export class LazyLoad implements ILazyLoad {
  private container: HTMLElement;
  private images: NodeListOf<HTMLImageElement>;
  private observer: IntersectionObserver;
  private _options: IOptions;

  constructor(ref: HTMLElement, { viewportOffset = 1000, removeAfterLoaded = false }: IOptions = {}) {
    this.container = ref;
    this._options = { viewportOffset, removeAfterLoaded };
    this.subscribeObserver();
  }

  private findImagesInContainer = () => {
    this.images = this.container?.querySelectorAll(LAZY_LOAD_IMAGE_SELECTOR);
  };

  private createPreview = (image: HTMLImageElement) => {
    const imageObject = document.createElement('img');
    const parsedSrc = imageCompressor.parse(image.dataset.src);
    const src = parsedSrc.valid && imageCompressor.getCompressedSrc(parsedSrc, 5);

    // TODO: сделать картинку для деволтного превью
    if (src) {
      const parent = image.parentElement;
      parent.classList.add('parent-preview');
      imageObject.className = 'lazy-load-preview';
      imageObject.src = src;

      parent.insertBefore(imageObject, image.nextSibling);
    }
  };

  private removePreview = (image: HTMLImageElement) => {
    const parent = image.parentElement;
    parent.classList.remove('parent-preview');
    const preview = image.nextSibling;
    if (preview && preview.nodeName !== '#text') {
      parent.removeChild(preview);
    }
  };

  private loadImage = (image: HTMLImageElement, src: string) => {
    image.src = src;
    image.removeAttribute('data-src');
    image.onload = () => {
      image.classList.remove('lazy-load');
      image.classList.add('lazy-loaded');
      this.removePreview(image);
    };
    image.onerror = () => {
      image.classList.add('is-hidden');
    };
  };

  private handleIntersect: IntersectionObserverCallback = (entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const img = entry.target as HTMLImageElement;

      if (img?.dataset?.src) {
        let src = img.dataset.src;

        const parsed = imageCompressor.parse(src);

        if (parsed.valid) {
          const key = src;
          src = imageCompressor.getCompressedSrc(parsed, +img?.dataset?.forcedCompression || this.getCompressValue());
          imagesCache.set(key, src);
        } else {
          src = parsed.defaultSource;
        }

        this.loadImage(img, src);
        observer.unobserve(img);
      }
    });
  };

  private subscribeObserver = () => {
    this.findImagesInContainer();

    this.observer = new IntersectionObserver(this.handleIntersect, {
      rootMargin: `0px 0px ${this._options.viewportOffset}px 0px`,
    });

    this.images.forEach((image) => {
      if (imagesCache.has(image.dataset.src)) {
        this.loadImage(image, imagesCache.get(image.dataset.src));
      } else {
        this.createPreview(image);
        this.observer.observe(image);
      }
    });
  };

  private getCompressValue = () => {
    return imageCompressor.getCompressValue({
      // check is mobile
      isMobile: window.matchMedia('(max-width: 768px)').matches,
      // check is table
      isTablet: window.matchMedia('(min-width: 768px and max-width: 1024px)').matches,
      // check is retina
      isRetina: window.devicePixelRatio >= 2,
      // set default compression
      compression: 1,
    });
  };

  public removeObserver = () => {
    this.observer.disconnect();
  };
}
